import { useState } from "react";
import axios from "axios";
import { MdOpenInNew } from "react-icons/md";

import formatClientDate from "utils/formatClientDate";
import formatDollars from "utils/formatDollars";
import Payment from "../types/Payment";
import { OutlineButton } from "components/button";
import { useTranslation } from 'react-i18next';

type Props = {
  payment: Payment;
  invalidateQueries: () => void;
};

const PaymentCard = ({ payment, invalidateQueries }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const tBase = "views.admin.quickbooks.online.components.PaymentCard";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const resyncPayment = async (paymentId: string) => {
    setIsLoading(true);
    await axios.post(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/sync-payment`,
      { paymentId }
    );
    invalidateQueries();
    setIsLoading(false);
  };

  const resetPayment = async (paymentId: string) => {
    setIsLoading(true);
    await axios.post(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reset-payment`,
      { paymentId }
    );
    invalidateQueries();
    setIsLoading(false);
  };

  const handleOpenPayment = () => {
    const paymentId = payment.id;
    window.open(`/payments/${paymentId}`);
  };

  const cardClass =
    payment.qbol_id && payment.qbol_synced_at
      ? "border-2 border-green-500 bg-green-200"
      : !payment.qbol_id && payment.qbol_message
      ? "border-2 border-red-500 bg-red-200"
      : payment.qbol_message && (!payment.qbol_synced_at || !payment.qbol_id)
      ? "border-2 border-orange-500"
      : "border-2 border-gray-500 bg-gray-50";

  return (
    <div
      className={`bg-white p-4 rounded-lg shadow mb-1 mr-1 text-xs ${cardClass}`}
    >
      <div className="flex flex-row justify-between">
        <div className="w-[50%]">
          <p>
            <div className="flex flex-row items-center">
              <strong>ID:</strong> {payment.id}
              <MdOpenInNew
                color="blue"
                className="cursor-pointer ml-2"
                onClick={handleOpenPayment}
              />
            </div>
          </p>
          <p>
            <strong>{tr("Invoice ID")}:</strong> {payment.fk_invoice_id}
          </p>
          <p>
            <strong>{tr("Customer ID")}:</strong> {payment.fk_bcustomer_id}
          </p>
          <p>
            <strong>{tr("Name")}:</strong> {payment.first_name} {payment.last_name}
          </p>
          <p>
            <strong>{tr("Company")}:</strong> {payment.company_name}
          </p>
          <p>
            <strong>{tr("Amount")}:</strong>{" "}
            {payment.amount
              ? formatDollars(payment.amount?.toString())
              : "Invalid Amount"}
          </p>
          <p>
            <strong>{tr("Date")}:</strong>{" "}
            {payment.received_on
              ? formatClientDate(payment.received_on?.toString())
              : tr("Invalid Date")}
          </p>
        </div>
        <div className="flex flex-col items-end w-[50%] text-right">
          <div>
            <strong>QBOL ID:</strong> {payment.qbol_id}
          </div>
          <div>
            <strong>{tr("Sync Token")}:</strong>
            {payment.qbol_sync_token}
          </div>
          <div>
            <strong>{tr("Message")}:</strong> {payment.qbol_message}
          </div>
          <div>
            <strong>{tr("Synced At")}:</strong>{" "}
            {payment.qbol_synced_at
              ? formatClientDate(payment.qbol_synced_at?.toString())
              : tr("Never")}
          </div>
          <div className="flex flex-row">
            <OutlineButton
              onClick={() => resetPayment(payment.id.toString())}
              disabled={isLoading}
              isLoading={isLoading}
            >
              {tr("Reset")}
            </OutlineButton>
            <OutlineButton
              className="ml-2"
              onClick={() => resyncPayment(payment.id.toString())}
              disabled={isLoading}
              isLoading={isLoading}
            >
              {tr("Re-sync")}
            </OutlineButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentCard;
